import { Navigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useGetResolvedFlagsQuery } from '../../store/api/featureFlagsApi';
import { useEffectiveTenant } from '../../hooks/useEffectiveTenant';

interface RoleGuardProps {
  children: React.ReactNode;
  /** Whitelist: solo estos roles tienen acceso. */
  allowedRoles?: string[];
  /** Blacklist: estos roles NO tienen acceso. */
  forbiddenRoles?: string[];
  /** Permiso que también concede acceso (OR con allowedRoles). Útil para grants puntuales (p.ej. demo). */
  permission?: string;
  /**
   * Feature flag que TAMBIÉN concede acceso (OR con allowedRoles), igual que `permission`.
   *
   * Es el camino para abrirle una pantalla de rol a alguien puntual sin cambiarle el rol
   * —cambiar el rol le movería medio CRM— y sin agregar un permiso a toda una categoría.
   * Primer caso: el Tablero, que es de soporte/admin y se le habilitó a un agente con su
   * tablero personal.
   */
  featureFlag?: string;
  /**
   * La pantalla es de Imperium (tenant nativo). Con el modo del tenant resuelto y distinto
   * de `native` rebota, sea cual sea el rol: esconder el ítem del menú no tapa la URL. La
   * plataforma sin tenant pasa; en modo soporte cuenta el tenant que se mira. (11/09/2026)
   */
  nativeTenantOnly?: boolean;
  redirectTo?: string;
}

export function RoleGuard({ children, allowedRoles, forbiddenRoles, permission, featureFlag, nativeTenantOnly, redirectTo }: RoleGuardProps) {
  const { user, hasPermission } = usePermissions();
  const { tenantMode } = useEffectiveTenant();
  // Sin flag pedido no se consulta nada: la mayoría de las rutas no lo usan.
  const { data: flags, isLoading: flagsLoading } = useGetResolvedFlagsQuery(undefined, { skip: !featureFlag });

  // Si no se especifica redirectTo, mandamos a la home según el rol del usuario.
  // Para 'booster_support' la home es /support, para los demás /dashboard.
  const fallback = redirectTo ?? (user?.role === 'booster_support' ? '/support' : '/dashboard');

  if (!user) return <Navigate to={fallback} replace />;
  // Antes del atajo de booster_admin: en modo soporte sobre un tenant Booster tampoco entra.
  if (nativeTenantOnly && tenantMode && tenantMode !== 'native') return <Navigate to={fallback} replace />;
  // platform_super (operador de plataforma, superset de roles) pasa cualquier guard de ROL.
  //
  // No cuando la ruta se protege SOLO por permiso (`allowedRoles` vacío): ahí el permiso es
  // toda la puerta, y un atajo por rol la deja abierta para quien no lo tiene. Es el caso
  // de la Zona de Peligro, que es cross-tenant.
  const soloPermiso = permission && allowedRoles?.length === 0;
  if (user.role === 'booster_admin' && !soloPermiso) return <>{children}</>;
  if (forbiddenRoles && forbiddenRoles.includes(user.role)) return <Navigate to={fallback} replace />;

  const roleOk = !allowedRoles || allowedRoles.includes(user.role);
  const permOk = permission ? hasPermission(permission) : false;
  // Mientras los flags cargan no se decide: rebotar acá manda a /dashboard a quien SÍ
  // tiene acceso, y ya no vuelve solo.
  if (!roleOk && !permOk && featureFlag && flagsLoading) return null;
  const flagOk = featureFlag ? flags?.[featureFlag] === true : false;
  if (!roleOk && !permOk && !flagOk) return <Navigate to={fallback} replace />;

  return <>{children}</>;
}
